import { useCallback, useEffect, useState } from 'react';
import { Leaderboard, LeaderboardDetails, LeaderboardType, PayoutStructure } from '@repo/common-types';
import StandardLayout from '@/common/layouts/StandardLayout';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Form,
  FormGroup,
  Label,
  Nav,
  NavItem,
  NavLink,
  PopoverBody,
  Row,
  TabContent,
  TabPane,
  UncontrolledPopover,
} from 'reactstrap';
import classnames from 'classnames';
import { BsInfoCircle } from 'react-icons/bs';
import { format } from 'date-fns';
import { AppDispatch, AppState } from '@/redux/root';
import { useDispatch, useSelector } from 'react-redux';
import { fetchLeaderboardOverview } from '@/services/leaderboards/leaderboard-thunks';
import { useForm, useFormState } from 'react-hook-form';
import { setStructureData } from '@/redux/slices/payoutStructure';
import { updateLeaderboardOverview } from '@/redux/slices/leaderboards';
import PayoutStructureForm from '../tournaments/PayoutStructures/components/PayoutStructureForm';

const LeaderboardConfigForm = ({ activeGlobalTab, type }) => {
  const [activeContentTab, setActiveContentTab] = useState<'details' | 'payout'>('details');

  const currentData = useSelector((state: AppState) => state.leaderboards.leaderboardOverview?.[type]);
  const payoutStructureFormState = useSelector((state: AppState) => state.payoutStructure);

  const dispatch = useDispatch<AppDispatch>();

  const form = useForm({
    defaultValues: {
      minRakeQualifierAmount: currentData?.minRakeQualifierAmount,
      minReseedAmount: currentData?.minReseedAmount,
      reseedPercentage: currentData?.reseedPercentage,
      maxBigBet: currentData?.maxBigBet,
      minBigBet: currentData?.minBigBet,
      maxBigBlind: currentData?.maxBigBlind,
      minBigBlind: currentData?.minBigBlind,
      payoutStructure: payoutStructureFormState,
    },
  });
  const formState = useFormState({ control: form.control });

  useEffect(() => {
    if (type === activeGlobalTab) {
      if (currentData?.payoutStructure) {
        dispatch(
          setStructureData({
            playersColumns: currentData?.payoutStructure?.playersColumns,
            rankRows: currentData?.payoutStructure?.rankRows,
          }),
        );
        return;
      }
      dispatch(
        setStructureData({
          name: 'Leaderboard Payout Structure',
          playersColumns: [
            {
              playersFrom: 1,
              playersTo: 10,
              percentages: [100, 0],
            },
            {
              playersFrom: 11,
              playersTo: 20,
              percentages: [80, 20],
            },
          ],
          rankRows: [
            {
              rankFrom: 1,
              rankTo: 1,
            },
            {
              rankFrom: 2,
              rankTo: 2,
            },
          ],
        }),
      );
    }
  }, [activeGlobalTab, currentData, type]);

  const onSubmit = useCallback(
    (data: Partial<LeaderboardDetails>) => {
      const updateLeaderboardOverviewPayload: Partial<Leaderboard> = {};

      if (formState.dirtyFields.minRakeQualifierAmount) {
        updateLeaderboardOverviewPayload.minRakeQualifierAmount = data.minRakeQualifierAmount;
      }

      if (formState.dirtyFields.minReseedAmount) {
        updateLeaderboardOverviewPayload.minReseedAmount = data.minReseedAmount;
      }

      if (formState.dirtyFields.reseedPercentage) {
        updateLeaderboardOverviewPayload.reseedPercentage = data.reseedPercentage;
      }

      if (payoutStructureFormState.isDirty) {
        updateLeaderboardOverviewPayload.prizeTierStructure = mapPayoutStructureToPrizeTier({
          playersColumns: payoutStructureFormState.playersColumns,
          rankRows: payoutStructureFormState.rankRows,
        });
      }

      if (formState.dirtyFields.maxBigBet) {
        updateLeaderboardOverviewPayload.maxBigBet = data.maxBigBet;
      }

      if (formState.dirtyFields.minBigBet) {
        updateLeaderboardOverviewPayload.minBigBet = data.minBigBet;
      }

      if (formState.dirtyFields.maxBigBlind) {
        updateLeaderboardOverviewPayload.maxBigBlind = data.maxBigBlind;
      }

      if (formState.dirtyFields.minBigBlind) {
        updateLeaderboardOverviewPayload.minBigBlind = data.minBigBlind;
      }

      dispatch(
        updateLeaderboardOverview({
          id: currentData?.id,
          ...updateLeaderboardOverviewPayload,
        }),
      );
    },
    [formState, payoutStructureFormState],
  );

  const mapPayoutStructureToPrizeTier = (
    payoutStructure: PayoutStructure,
  ): PrismaJson.LeaderboardPrizeTierStructure => {
    const prizeTierStructure: PrismaJson.LeaderboardPrizeTierStructure = {};
    payoutStructure.playersColumns.forEach((column) => {
      prizeTierStructure[column.playersTo] = column.percentages;
    });
    return prizeTierStructure;
  };

  const renderInfoIcon = (id: string, content: string) => (
    <>
      <BsInfoCircle id={id} className="text-muted ms-2" style={{ cursor: 'pointer' }} />
      <UncontrolledPopover placement="top" target={id} trigger="hover">
        <PopoverBody>{content}</PopoverBody>
      </UncontrolledPopover>
    </>
  );

  const renderConfigSection = () => (
    <Card className="mb-4">
      <CardHeader>
        <h5 className="mb-0">Configuration Settings</h5>
      </CardHeader>
      <CardBody>
        <Form>
          <FormGroup>
            <Label for="minRakeQualifier">
              Minimum Rake Qualifier Amount
              {renderInfoIcon(
                `minRakeQualifier-${activeGlobalTab}`,
                'Minimum rake amount required for a player to qualify for the leaderboard',
              )}
            </Label>
            <input
              className="form-control"
              {...form.register('minRakeQualifierAmount', {
                valueAsNumber: true,
                onChange: (e) => {
                  form.setValue('minRakeQualifierAmount', e.target.value, { shouldDirty: true });
                },
              })}
              name="minRakeQualifierAmount"
              type="number"
            />
          </FormGroup>

          <FormGroup>
            <Label for="minReseedAmount">
              Minimum Reseed Amount
              {renderInfoIcon(
                `minReseedAmount-${activeGlobalTab}`,
                'Minimum amount that will be added to the next leaderboard period',
              )}
            </Label>
            <input
              className="form-control"
              {...form.register('minReseedAmount', {
                valueAsNumber: true,
                onChange: (e) => {
                  form.setValue('minReseedAmount', e.target.value, { shouldDirty: true });
                },
              })}
              name="minReseedAmount"
              type="number"
            />
          </FormGroup>

          <FormGroup>
            <Label for="reseedPercentage">
              Reseed Percentage (%)
              {renderInfoIcon(
                `reseedPercentage-${activeGlobalTab}`,
                'Percentage of the current prize pool that will carry over to the next period',
              )}
            </Label>
            <input
              className="form-control"
              {...form.register('reseedPercentage', {
                valueAsNumber: true,
                onChange: (e) => {
                  form.setValue('reseedPercentage', e.target.value, { shouldDirty: true });
                },
              })}
              name="reseedPercentage"
              type="number"
            />
          </FormGroup>

          <Row>
            <Col md={6}>
              <FormGroup>
                <Label for="minBigBet">
                  Min Big Bet
                  {renderInfoIcon(`minBigBet-${activeGlobalTab}`, 'Minimum big bet for the leaderboard')}
                </Label>
                <input
                  className="form-control"
                  {...form.register('minBigBet', {
                    valueAsNumber: true,
                    onChange: (e) => {
                      form.setValue('minBigBet', e.target.value, { shouldDirty: true });
                    },
                  })}
                  name="minBigBet"
                  type="number"
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="maxBigBet">
                  Max Big Bet
                  {renderInfoIcon(`maxBigBet-${activeGlobalTab}`, 'Maximum big bet for the leaderboard')}
                </Label>
                <input
                  className="form-control"
                  {...form.register('maxBigBet', {
                    valueAsNumber: true,
                    onChange: (e) => {
                      form.setValue('maxBigBet', e.target.value, { shouldDirty: true });
                    },
                  })}
                  name="maxBigBet"
                  type="number"
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label for="maxBigBlind">
                  Max Big Blind
                  {renderInfoIcon(`maxBigBlind-${activeGlobalTab}`, 'Maximum big blind for the leaderboard')}
                </Label>
                <input
                  className="form-control"
                  {...form.register('maxBigBlind', {
                    valueAsNumber: true,
                    onChange: (e) => {
                      form.setValue('maxBigBlind', e.target.value, { shouldDirty: true });
                    },
                  })}
                  name="maxBigBlind"
                  type="number"
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="minBigBlind">
                  Min Big Blind
                  {renderInfoIcon(`minBigBlind-${activeGlobalTab}`, 'Minimum big blind for the leaderboard')}
                </Label>
                <input
                  className="form-control"
                  {...form.register('minBigBlind', {
                    valueAsNumber: true,
                    onChange: (e) => {
                      form.setValue('minBigBlind', e.target.value, { shouldDirty: true });
                    },
                  })}
                  name="minBigBlind"
                  type="number"
                />
              </FormGroup>
            </Col>
          </Row>
        </Form>
      </CardBody>
    </Card>
  );

  const renderCurrentDataSection = () => (
    <Card className="mb-4">
      <CardHeader>
        <h5 className="mb-0">Current Leaderboard Information</h5>
      </CardHeader>
      <CardBody>
        <Row>
          <Col md={6}>
            <div className="mb-3">
              <Label className="text-muted mb-1">Status</Label>
              <div>
                <Badge color={currentData?.status === 'ACTIVE' ? 'success' : 'secondary'} className="px-2 py-1">
                  {currentData?.status}
                </Badge>
              </div>
            </div>
            <div className="mb-3">
              <Label className="text-muted mb-1">Start Date</Label>
              <div className="fw-bold">{format(currentData?.startDate, 'MMM dd, yyyy')}</div>
            </div>
            <div className="mb-3">
              <Label className="text-muted mb-1">End Date</Label>
              <div className="fw-bold">{format(currentData?.endDate ?? new Date(), 'MMM dd, yyyy')}</div>
            </div>
          </Col>
          <Col md={6}>
            <div className="mb-3">
              <Label className="text-muted mb-1">Total Prize Pool</Label>
              <div className="fw-bold fs-5">${currentData?.totalBalance.toLocaleString()}</div>
            </div>
            <div className="mb-3">
              <Label className="text-muted mb-1">Current Reseed Amount</Label>
              <div className="fw-bold fs-5">${currentData?.currentReseedAmount.toLocaleString()}</div>
            </div>
          </Col>
        </Row>
      </CardBody>
    </Card>
  );

  if (!currentData) {
    return null;
  }

  return (
    <>
      <Nav tabs className="mb-4">
        <NavItem>
          <NavLink
            className={classnames({ active: activeContentTab === 'details' })}
            onClick={() => setActiveContentTab('details')}
            style={{ cursor: 'pointer' }}
          >
            Details
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            className={classnames({ active: activeContentTab === 'payout' })}
            onClick={() => setActiveContentTab('payout')}
            style={{ cursor: 'pointer' }}
          >
            Payout Structure
          </NavLink>
        </NavItem>
      </Nav>

      <Button className="mb-4" color="primary" onClick={form.handleSubmit(onSubmit)}>
        Save Changes
      </Button>

      <TabContent activeTab={activeContentTab}>
        <TabPane tabId="details">
          {renderCurrentDataSection()}
          {renderConfigSection()}
        </TabPane>
        <TabPane tabId="payout">
          <PayoutStructureForm />
        </TabPane>
      </TabContent>
    </>
  );
};

const LeaderboardConfigPage = () => {
  const [activeGlobalTab, setActiveGlobalTab] = useState<LeaderboardType>(LeaderboardType.MICRO);

  const dispatch = useDispatch<AppDispatch>();
  useEffect(() => {
    dispatch(fetchLeaderboardOverview() as any);
  }, []);

  const leaderboardOverview = useSelector((state: AppState) => state.leaderboards.leaderboardOverview);

  if (!leaderboardOverview) {
    return null;
  }

  return (
    <StandardLayout pageTitle="leaderboard configuration">
      <Nav tabs className="mb-4">
        <NavItem>
          <NavLink
            className={classnames({ active: activeGlobalTab === LeaderboardType.MICRO })}
            onClick={() => setActiveGlobalTab(LeaderboardType.MICRO)}
            style={{ cursor: 'pointer' }}
          >
            Micro
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            className={classnames({ active: activeGlobalTab === LeaderboardType.MEDIUM })}
            onClick={() => setActiveGlobalTab(LeaderboardType.MEDIUM)}
            style={{ cursor: 'pointer' }}
          >
            Medium
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            className={classnames({ active: activeGlobalTab === LeaderboardType.HIGH })}
            onClick={() => setActiveGlobalTab(LeaderboardType.HIGH)}
            style={{ cursor: 'pointer' }}
          >
            High
          </NavLink>
        </NavItem>
      </Nav>

      <TabContent activeTab={activeGlobalTab}>
        <TabPane tabId={LeaderboardType.MICRO}>
          <LeaderboardConfigForm activeGlobalTab={activeGlobalTab} type={LeaderboardType.MICRO} />
        </TabPane>

        <TabPane tabId={LeaderboardType.MEDIUM}>
          <LeaderboardConfigForm activeGlobalTab={activeGlobalTab} type={LeaderboardType.MEDIUM} />
        </TabPane>

        <TabPane tabId={LeaderboardType.HIGH}>
          <LeaderboardConfigForm activeGlobalTab={activeGlobalTab} type={LeaderboardType.HIGH} />
        </TabPane>
      </TabContent>
    </StandardLayout>
  );
};

export default LeaderboardConfigPage;
